// The beach ramp mesh (shoreRamp.ts): ONE mesh / ONE draw call for the whole coast. Sand shaded dry -> wet by
// its height above the live water surface (the same vertex wave the water plane runs, so the wet line and the
// foam follow the real waterline as the waves move it), blended into the land's own shore band at the top.
// Honours the water clock freeze (reduced motion / particle effects off) because it reads `waterTime`.

import { useEffect, useMemo, useRef } from 'react';
import * as THREE from 'three';
import { RenderLayer } from '../RenderLayer.js';
import { waterTime } from '../TerrainWater.js';
import {
  CLOUD_SHADOW_GLSL_MAIN,
  CLOUD_SHADOW_GLSL_PARS,
  cloudShadowUniforms,
} from '../cloudShadow.js';
import { fogTone } from '../terrainEdges.js';
import type { ShoreBoard } from './shoreBoard.js';
import { SAND_DRY, SAND_WET } from './shoreColors.js';
import { RAMP_VERTS_PER_EDGE } from './shoreRamp.js';

const f = (v: number) => v.toFixed(5);
const vec3 = (hex: string) => {
  const c = new THREE.Color(hex);
  return `vec3(${f(c.r)}, ${f(c.g)}, ${f(c.b)})`;
};

/** Sand shader: MeshBasicMaterial + injected chunks, so scene fog and tone mapping behave like the terrain. */
export function createRampMaterial(): THREE.MeshBasicMaterial {
  const mat = new THREE.MeshBasicMaterial({
    color: 'rgb(255,255,255)',
    fog: true,
    polygonOffset: true,
    polygonOffsetFactor: -1,
    polygonOffsetUnits: -1,
  });
  mat.onBeforeCompile = (shader) => {
    shader.uniforms.uTime = waterTime;
    Object.assign(shader.uniforms, cloudShadowUniforms);
    shader.vertexShader = `uniform float uTime;
      attribute vec4 aInfo;
      attribute vec2 aTone;
      varying vec4 _vInfo;
      varying vec2 _vTone;
      varying vec3 _vLocal;
      ${shader.vertexShader}`.replace(
      '#include <begin_vertex>',
      `#include <begin_vertex>
      _vInfo = aInfo;
      _vTone = aTone;
      _vLocal = position;`,
    );
    shader.fragmentShader = `uniform float uTime;
      varying vec4 _vInfo;
      varying vec2 _vTone;
      varying vec3 _vLocal;
      ${CLOUD_SHADOW_GLSL_PARS}
      ${shader.fragmentShader}`.replace(
      '#include <color_fragment>',
      `#include <color_fragment>
      {
        // The same wave the water plane runs (TerrainWater), so the wet line follows the real waterline.
        float _x = _vLocal.x;
        float _z = _vLocal.z;
        float _w = sin(_x * 2.5 + uTime) * cos(_z * 2.5 + uTime * 0.7);
        _w += 0.6 * sin(_x * 5.0 + uTime * 1.3) * sin(_z * 5.0 - uTime * 0.9);
        _w += 0.35 * sin((_x + _z) * 1.1 + uTime * 0.5);
        float _rel = _vLocal.y - (_vInfo.y + _w * _vInfo.z);
        float _bn = fract(sin(dot(vec2(_x, _z) * 0.45, vec2(127.1, 311.7))) * 43758.5453);
        _bn += fract(sin(dot(vec2(_x, _z) * 0.9 + 2.0, vec2(269.5, 183.3))) * 23421.6312);
        _bn *= 0.5;
        float _grain = 0.93 + 0.14 * _bn;
        // Dry sand up the beach, darker and bluer where the swash wets it, a bright band of foam at the line.
        float _wob = 0.012 * sin(_x * 9.0 + _z * 7.0 + uTime * 0.8);
        float _wet = 1.0 - smoothstep(-0.01, 0.09, _rel + _wob);
        vec3 _sand = mix(${vec3(SAND_DRY)}, ${vec3(SAND_WET)}, _wet);
        float _swash = 0.018 + 0.03 * (0.5 + 0.5 * sin(uTime * 0.9 + _x * 1.7 + _z * 1.3));
        float _foam = (1.0 - smoothstep(0.0, 0.045, abs(_rel - _swash)))
                    * (1.0 - smoothstep(0.0, 0.04, -_rel - 0.12));
        _sand = mix(_sand, vec3(0.95, 0.97, 0.95), _foam * 0.9);
        // Underwater part of the ramp: shift to the shallow-water tone so it never reads as dry sand through a
        // trough (the opaque water plane hides it anyway).
        float _lum = dot(_sand, vec3(0.2126, 0.7152, 0.0722));
        _sand = mix(vec3(_lum), _sand, _vTone.y) * _vTone.x * _grain;
        diffuseColor.rgb = _sand;
        vec3 _vWorldPos = _vLocal;
        ${CLOUD_SHADOW_GLSL_MAIN}
      }`,
    );
  };
  mat.customProgramCacheKey = () => 'shore-ramp';
  return mat;
}

export function ShorelineMesh({
  board,
  visibleCoords,
  exploredCoords,
}: {
  readonly board: ShoreBoard;
  readonly visibleCoords?: ReadonlySet<string> | null;
  readonly exploredCoords?: ReadonlySet<string>;
}) {
  const material = useMemo(createRampMaterial, []);
  const toneRef = useRef<THREE.BufferAttribute | null>(null);
  // One geometry per terrain signature (never per publish).
  const geometry = useMemo(() => {
    const { ramp } = board;
    const g = new THREE.BufferGeometry();
    g.setAttribute('position', new THREE.BufferAttribute(ramp.positions, 3));
    g.setAttribute('aInfo', new THREE.BufferAttribute(ramp.info, 4));
    const tone = new THREE.BufferAttribute(new Float32Array((ramp.positions.length / 3) * 2), 2);
    g.setAttribute('aTone', tone);
    g.setIndex(new THREE.BufferAttribute(ramp.indices, 1));
    g.computeBoundingSphere();
    toneRef.current = tone;
    return g;
  }, [board.signature]);
  useEffect(() => () => geometry.dispose(), [geometry]);

  // Fog of war: update the per-vertex tone in place (no reallocation) when visibility changes.
  useEffect(() => {
    const tone = toneRef.current;
    if (!tone) return;
    const arr = tone.array as Float32Array;
    let changed = false;
    board.ramp.tileKeys.forEach((key, e) => {
      const [l, s] = fogTone(key, visibleCoords, exploredCoords as ReadonlySet<string> | undefined);
      const first = e * RAMP_VERTS_PER_EDGE * 2;
      // visibleCoords is a fresh Set on most publishes: only re-upload the buffer when a tile's tone moved.
      if (arr[first] === l && arr[first + 1] === s) return;
      changed = true;
      for (let v = 0; v < RAMP_VERTS_PER_EDGE; v++) {
        const o = (e * RAMP_VERTS_PER_EDGE + v) * 2;
        arr[o] = l;
        arr[o + 1] = s;
      }
    });
    if (changed) tone.needsUpdate = true;
  }, [board.signature, geometry, visibleCoords, exploredCoords]);

  if (board.ramp.edgeCount === 0) return null;
  return (
    <mesh
      name="shore-ramp"
      geometry={geometry}
      material={material}
      renderOrder={RenderLayer.Terrain + 0.5}
      frustumCulled={false}
      raycast={() => null}
    />
  );
}
